import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PropertyCard, type PropertyCardData } from "./PropertyCard";
import { Bell, SlidersHorizontal } from "lucide-react";
import { SavedSearchDialog } from "./SavedSearchDialog";
import { useI18n } from "@/lib/i18n";
import { fetchPublicProperties } from "@/lib/publicData";

export type ListFilters = {
  operation?: string;
  property_type?: string;
  location_slug?: string;
  project_name?: string;
};

type Sort = "recent" | "price_asc" | "price_desc";

export function PropertyListView({
  filters,
  initialProperties,
}: {
  filters: ListFilters;
  initialProperties?: PropertyCardData[];
}) {
  const { t } = useI18n();
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [beds, setBeds] = useState<string>("");
  const [minArea, setMinArea] = useState("");
  const [sort, setSort] = useState<Sort>("recent");
  const [currency, setCurrency] = useState<string>("");
  const [alertOpen, setAlertOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["public-properties", filters],
    queryFn: () => fetchPublicProperties(filters),
    initialData: initialProperties as never,
  });

  const filtered = useMemo(() => {
    let list = (data ?? []) as (PropertyCardData & { created_at: string; area_m2?: number | null })[];
    if (currency) list = list.filter((p) => p.price_currency === currency);
    if (minPrice) list = list.filter((p) => p.price_amount >= Number(minPrice));
    if (maxPrice) list = list.filter((p) => p.price_amount <= Number(maxPrice));
    if (beds) list = list.filter((p) => (p.bedrooms ?? 0) >= Number(beds));
    if (minArea) list = list.filter((p) => (p.area_m2 ?? 0) >= Number(minArea));
    if (sort === "price_asc") list = [...list].sort((a, b) => a.price_amount - b.price_amount);
    else if (sort === "price_desc") list = [...list].sort((a, b) => b.price_amount - a.price_amount);
    return list;
  }, [data, minPrice, maxPrice, beds, minArea, sort, currency]);

  const alertFilters = {
    ...filters,
    currency: currency || undefined,
    min_price: minPrice || undefined,
    max_price: maxPrice || undefined,
    min_beds: beds || undefined,
    min_area_m2: minArea || undefined,
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
      <aside className="lg:sticky lg:top-20 lg:h-fit">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
            <SlidersHorizontal className="h-4 w-4" /> {t("filters.title")}
          </div>

          <div className="space-y-4">
            <Field label={t("filters.currency")}>
              <select aria-label={t("filters.currency")} value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls}>
                <option value="">{t("filters.all")}</option>
                <option value="USD">USD</option>
                <option value="PYG">Gs.</option>
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label={t("filters.minPrice")}>
                <input type="number" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("filters.maxPrice")}>
                <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className={inputCls} />
              </Field>
            </div>
            <Field label={t("filters.beds")}>
              <select aria-label={t("filters.beds")} value={beds} onChange={(e) => setBeds(e.target.value)} className={inputCls}>
                <option value="">{t("filters.any")}</option>
                <option value="1">1+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
                <option value="4">4+</option>
              </select>
            </Field>
            <Field label={t("filters.minArea")}>
              <input
                type="number"
                value={minArea}
                onChange={(e) => setMinArea(e.target.value)}
                className={inputCls}
                placeholder="0"
              />
            </Field>
            <button
              onClick={() => { setMinPrice(""); setMaxPrice(""); setBeds(""); setCurrency(""); setMinArea(""); }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs hover:bg-secondary"
            >
              {t("filters.clear")}
            </button>
            <button
              onClick={() => setAlertOpen(true)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent px-3 py-2.5 text-xs font-semibold text-accent-foreground hover:brightness-110"
            >
              <Bell className="h-3.5 w-3.5" /> {t("filters.alerts")}
            </button>
          </div>
        </div>
      </aside>

      {alertOpen && (
        <SavedSearchDialog filters={alertFilters} onClose={() => setAlertOpen(false)} />
      )}

      <div>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            {isLoading ? t("list.loading") : `${filtered.length} ${t("list.count")}`}
          </div>
          <select aria-label={t("sort.label")} value={sort} onChange={(e) => setSort(e.target.value as Sort)} className="rounded-md border border-input bg-background px-3 py-1.5 text-xs">
            <option value="recent">{t("sort.recent")}</option>
            <option value="price_asc">{t("sort.price_asc")}</option>
            <option value="price_desc">{t("sort.price_desc")}</option>
          </select>
        </div>

        {isLoading ? (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-xl bg-secondary/60" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-secondary/40 p-12 text-center text-sm text-muted-foreground">
            {t("list.empty")}
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
